/* eslint-disable max-len */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import logger from '../common/logger';
import {
  RETRY_COUNT, LEARN_LIVE_ENROLLMENT_HOST, LEARN_LIVE_COACHING_REQUEST_SUBMIT_VERSION, LEARN_LIVE_SERVICES_HOST,
  coachTypeConstants,
} from '../common/constants';
import { getEnvironmentConstant } from '../common/utilities';
import { getStaticContent } from './universalDataFetchers';
import fetchLearnLiveCourseById from '../requesting/fetchLearnLiveCourseById';
import fetchFastTrackPlanById from '../requesting/fetchFastTrackPlanById';

axiosRetry(axios, { retries: RETRY_COUNT });

export const adaptCoachingRequestModelToServiceModel = (summary) => {
  const coachingSubType = summary.coachingType.product.length > 0 ? summary.coachingType.product : new Array(summary.coachingType.topic);

  const returnValue = {
    CustomerId: summary.customerId,
    CoachingType: { id: summary?.coachingType?.type.id },
    CoachingSubTypes: coachingSubType,
    Objective: summary.objectives,
    CompanyName: summary.companyName,
    SubscriptionTypeId: summary.subscriptionTypeId || 1,
  };

  if (summary.subscription?.id) {
    // returnValue.SubscriptionId = summary.subscription.id;
    returnValue.GroupOwnerExternalId = summary.subscription.id;
    returnValue.GroupOwnerExternalFieldName = 'subscriptionId';
  }

  if (summary.availability?.length > 0) {
    returnValue.DateTimeAvailabilities = summary.availability;
  }

  if (summary.requestItem?.sys?.id) {
    returnValue.RequestItemId = summary.requestItem.sys.id;
  }

  if (summary.requestItem?.meta?.type === 'fastTrackLearningPlan') {
    returnValue.RequestItemId = summary.requestItem.meta.id;
  }

  if (summary.coachingType?.type?.value === coachTypeConstants.FAST_TRACK && summary.coachingType?.topic?.id) {
    returnValue.RequestItemId = summary.coachingType?.topic?.id;
    returnValue.CoachingSubTypes = [];
  }

  return returnValue;
};
const getLearnLiveServiceHost = ({ env }) => (`${getEnvironmentConstant({
  env,
  key: LEARN_LIVE_ENROLLMENT_HOST,
})}`);

export const postCoachingRequest = createAsyncThunk(
  'postCoachingRequest',
  ({
    oa2Token,
    env,
    data,
  },
  { rejectWithValue }) => {
    const host = `${getLearnLiveServiceHost({ env })}/coachingrequest/${getEnvironmentConstant({
      env,
      key: LEARN_LIVE_COACHING_REQUEST_SUBMIT_VERSION,
    })}`;
    const headers = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${oa2Token}`,
      },
    };
    const dataToSend = adaptCoachingRequestModelToServiceModel(data);
    return axios.post(host, dataToSend, headers).catch((err) => rejectWithValue(err));
  },
);

export const postCoachingReferral = createAsyncThunk(
  'postCoachingReferral',
  ({
    oa2Token,
    env,
    data,
  },
  { rejectWithValue }) => {
    const host = `${getLearnLiveServiceHost({ env })}/coachingrequest/referral/v2`;
    const headers = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${oa2Token}`,
      },
    };
    const dataToSend = adaptCoachingRequestModelToServiceModel(data);
    return axios.post(host, dataToSend, headers).catch((err) => rejectWithValue(err));
  },
);

export const fetchIdentityData = createAsyncThunk(
  'fetchIdentityData',
  ({ env, token, customerEmail }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const identityRequestUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/${customerEmail ? 'customer' : 'submitter'}/v1`;
    if (customerEmail) {
      return axios
        .post(
          identityRequestUrl,
          { email: customerEmail },
          {
            headers: {
              ...headers,
            },
          },
        )
        .catch((err) => rejectWithValue(err));
    }
    return axios
      .get(identityRequestUrl, {
        headers: {
          ...headers,
        },
      })
      .catch((err) => rejectWithValue(err));
  },
);

export const fetchCoachingTypeData = createAsyncThunk(
  'fetchCoachingTypeData',
  async ({ env, token, subscriptionTypeId }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const coachingTypeUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/scope/v1${subscriptionTypeId ? `?subscriptionTypeId=${subscriptionTypeId}` : ''}`;
    try {
      return axios
        .get(coachingTypeUrl, {
          headers: {
            ...headers,
          },
        });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchFastTrackTopicsData = createAsyncThunk(
  'fetchFastTrackTopicsData',
  async ({ env, token }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const fastTrackTopicsUrl = `${getEnvironmentConstant({ env, key: LEARN_LIVE_SERVICES_HOST })}/fasttrack/templates/v1`;
    try {
      return await axios.get(fastTrackTopicsUrl, { headers: { ...headers } });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchSubscriptions = createAsyncThunk(
  'fetchSubscriptions',
  ({
    env, token, userId = null,
  }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const host = getEnvironmentConstant({ key: LEARN_LIVE_SERVICES_HOST, env });
    const url = `${host}/cso/subscriptions/v1${userId ? `/${userId}` : ''}`;
    try {
      return axios
        .get(url, {
          headers: {
            ...headers,
          },
        }).catch((err) => rejectWithValue(err));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchCoachingRequestEditorialData = createAsyncThunk(
  'fetchCoachingRequestEditorialData',
  ({
    slugs, env, isPreview, isSSR,
  }, { rejectWithValue }) => getStaticContent({
    slugs, env, isPreview, isSSR,
  }).then((res) => (res)).catch((err) => (rejectWithValue(err))),
);

export const fetchCourseDetailData = createAsyncThunk(
  'fetchCourseDetailData',
  ({ entryId, env }, { rejectWithValue }) => fetchLearnLiveCourseById({ entryId, env }).then((res) => (res)).catch((err) => (rejectWithValue(err))),
);

export const fetchFastTrackPlanMetaData = createAsyncThunk(
  'fetchFastTrackPlanMetaData',
  ({ entryId, env }, { rejectWithValue }) => fetchFastTrackPlanById({ entryId, env }).then((res) => (res)).catch((err) => (rejectWithValue(err))),
);

export const coachingRequestSlice = createSlice({
  name: 'coachingRequestSlice',
  initialState: {
    identityData: {
      data: {},
      hasFetched: false,
      hasError: false,
      isLoaderVisible: false,
      isIdentityResolved: false,
    },
    referrer: {
      data: null,
    },
    coachingTypeData: {
      data: [],
      hasFetched: false,
      hasError: false,
      isLoaderVisible: false,
    },
    fastTrackTopicsData: {
      data: [],
      hasFetched: false,
      hasError: false,
      isLoaderVisible: false,
    },
    subscriptionData: {
      data: [],
      hasFetched: false,
      hasError: false,
      isLoaderVisible: false,
    },
    requestSummary: {
      customerId: null,
      companyName: '',
      coachingType: {
        type: {},
        product: [],
        topic: {},
      },
      objectives: '',
      availability: [],
      subscriptionTypeId: null,
      subscription: {},
      requestItem: {},
      hourType: false,
    },
    isTermsAccepted: false,
    postingCoachingRequestError: '',
    postingCoachingRequest: false,
  },
  reducers: {
    setCoachingType: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.coachingType = action?.payload;
      });
      return newState;
    },
    setCompanyName: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary = { ...draftState.requestSummary, ...{ companyName: action?.payload } };
      });
      return newState;
    },
    setCoachingSubType: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.coachingSubType = action.payload;
      });
      return newState;
    },
    setAvailability: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.availability = action?.payload;
      });
      return newState;
    },
    setObjectives: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.objectives = action?.payload;
      });
      return newState;
    },
    setTermsAccepted: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.isTermsAccepted = action?.payload;
      });
      return newState;
    },
    setReferrer: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.referrer.data = action?.payload;
        draftState.referrer.hasFetched = true;
        draftState.identityData.isIdentityResolved = false;
      });
      return newState;
    },
    setIdentityResolved: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.identityData.isIdentityResolved = action?.payload;
      });
      return newState;
    },
    setSubscriptionTypeId: (state, action) => {
      const newState = produce(state, (draftState) => { draftState.requestSummary.subscriptionTypeId = action?.payload; });
      return newState;
    },
    setSelectedSubscription: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.subscription = action?.payload;
      });
      return newState;
    },
    setHourType: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.hourType = action?.payload;
      });
      return newState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchIdentityData.pending, (state) => ({
      ...state,
      identityData: {
        ...state.identityData,
        isLoaderVisible: true,
        hasFetched: false,
      },
    }));
    builder.addCase(fetchIdentityData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          if (draftState?.identityData?.data?.userId === data?.data?.userId) {
            // Merge
            draftState.identityData.data = {
              ...draftState.identityData.data,
              ...data.data,
            };
          } else {
            // Overwrite
            draftState.identityData.data = data.data;
            draftState.requestSummary.customerId = data.data.userId;
          }
          draftState.identityData.isReferredUserLoaded = (typeof action?.meta?.arg?.customerEmail === 'string' && action?.meta?.arg?.customerEmail?.length > 0);

          if (data.data.isPremiumSubscriber || data.data.isAdministrator) {
            draftState.requestSummary.subscriptionTypeId = 1;
          }
          if (data.data.isEnterpriseAdmin || data.data.isEnterpriseSubscriber) {
            draftState.requestSummary.subscriptionTypeId = 2;
          }
          draftState.identityData.isLoaderVisible = false;
          draftState.identityData.hasFetched = true;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(fetchIdentityData.rejected, (state, action) => {
      logger.error('Error in fetchIdentityData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.identityData.hasError = true;
        draftState.identityData.isLoaderVisible = false;
        draftState.identityData.hasFetched = true;
      });

      return newState;
    });
    builder.addCase(postCoachingRequest.pending, (state) => {
      const newState = produce(state, (draftState) => {
        delete draftState.postingCoachingRequestError;
        draftState.postingCoachingRequest = true;
      });

      return newState;
    });
    builder.addCase(postCoachingRequest.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.data = data.data?.classData;
          draftState.postingCoachingRequest = false;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(postCoachingRequest.rejected, (state, action) => {
      logger.error('Error in postCoachingRequest: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.postingCoachingRequestError = action?.payload?.response?.status === 401 ? 'Invalid authorization code.' : 'Authorization failure. Please try again later. If failure persists, please contact your administrator.';
        draftState.postingCoachingRequest = false;
      });

      return newState;
    });
    builder.addCase(postCoachingReferral.pending, (state) => {
      const newState = produce(state, (draftState) => {
        delete draftState.postingCoachingRequestError;
        draftState.postingCoachingRequest = true;
      });

      return newState;
    });
    builder.addCase(postCoachingReferral.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.data = data.data?.classData;
          draftState.postingCoachingRequest = false;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(postCoachingReferral.rejected, (state, action) => {
      logger.error('Error in postCoachingReferral: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.postingCoachingRequestError = action?.payload?.response?.status === 401 ? 'Invalid authorization code.' : 'Authorization failure. Please try again later. If failure persists, please contact your administrator.';
        draftState.postingCoachingRequest = false;
      });

      return newState;
    });
    builder.addCase(fetchCoachingTypeData.pending, (state) => ({
      ...state,
      coachingTypeData: { isLoaderVisible: true },
    }));
    builder.addCase(fetchCoachingTypeData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error && data?.data?.CoachingTypes) {
        let coachingType = [];
        const newState = produce(state, (draftState) => {
          // Merge
          Object.values(data?.data?.CoachingTypes)
            .forEach((item) => {
              // Sort coachingSubTypes by label
              if (Array.isArray(item.coachingSubTypes)) {
                item.coachingSubTypes.sort((a, b) => a.label.localeCompare(b.label));
              }
              coachingType = [...coachingType, {
                children: item.label,
                value: item.key,
                id: item.id,
                coachingSubtype: item.coachingSubTypes,
              }];
            });
          draftState.coachingTypeData.data = coachingType;
          draftState.coachingTypeData.isLoaderVisible = false;
          draftState.coachingTypeData.hasFetched = true;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchCoachingTypeData.rejected, (state, action) => {
      logger.error('Error in fetchCoachingTypeData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.coachingTypeData.isLoaderVisible = false;
        draftState.coachingTypeData.hasError = true;
      });

      return newState;
    });
    builder.addCase(fetchFastTrackTopicsData.pending, (state) => ({
      ...state,
      fastTrackTopicsData: { isLoaderVisible: true },
    }));
    builder.addCase(fetchFastTrackTopicsData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (data.status === 200) {
        const newState = produce(state, (draftState) => {
          draftState.fastTrackTopicsData.data = data.data.items;
          draftState.fastTrackTopicsData.isLoaderVisible = false;
          draftState.fastTrackTopicsData.hasFetched = true;
        });
        return newState;
      }
      const newState = produce(state, (draftState) => {
        draftState.fastTrackTopicsData.data = [];
        draftState.fastTrackTopicsData.isLoaderVisible = false;
        draftState.fastTrackTopicsData.hasFetched = true;
      });
      return newState;
    });
    builder.addCase(fetchFastTrackTopicsData.rejected, (state, action) => {
      logger.error('Error in fetchFastTrackTopicsData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.fastTrackTopicsData.isLoaderVisible = false;
        draftState.fastTrackTopicsData.hasError = true;
      });
      return newState;
    });
    builder.addCase(fetchCoachingRequestEditorialData.pending, (state) => ({
      ...state,
    }));
    builder.addCase(fetchCoachingRequestEditorialData.fulfilled, (state, action) => {
      const data = action?.payload;
      if (!data?.error) {
        const newState = produce(state, (draftState) => {
          draftState.editorial = data;
        });
        return newState;
      }

      return state;
    });
    builder.addCase(fetchCoachingRequestEditorialData.rejected, (state, action) => {
      logger.error('Error in fetchCoachingRequestEditorialData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.hasEditorialError = true;
      });
      return newState;
    });
    builder.addCase(fetchSubscriptions.pending, (state) => ({
      ...state,
      subscriptionData: { isLoaderVisible: true },
    }));
    builder.addCase(fetchSubscriptions.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.subscriptionData.data = data;
          draftState.subscriptionData.isLoaderVisible = false;
          draftState.subscriptionData.hasFetched = true;
          if (data?.data?.length === 1) {
            [draftState.requestSummary.subscription] = data.data;
          }
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchSubscriptions.rejected, (state, action) => {
      logger.error('Error in fetchSubscriptions: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.subscriptionData.isLoaderVisible = false;
        draftState.subscriptionData.hasError = true;
      });
      return newState;
    });
    builder.addCase(fetchCourseDetailData.pending, (state) => ({
      ...state,
      requestSummary: { ...state.requestSummary, requestItem: { isLoaderVisible: true } },
    }));
    builder.addCase(fetchCourseDetailData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.requestSummary.requestItem = data;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchCourseDetailData.rejected, (state, action) => {
      logger.error('Error in fetchCourseDetailData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.requestItem.isLoaderVisible = false;
        draftState.requestSummary.requestItem.hasError = true;
      });
      return newState;
    });
    builder.addCase(fetchFastTrackPlanMetaData.pending, (state) => ({
      ...state,
      requestSummary: { ...state.requestSummary, requestItem: { isLoaderVisible: true } },
    }));
    builder.addCase(fetchFastTrackPlanMetaData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.requestSummary.requestItem = data;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchFastTrackPlanMetaData.rejected, (state, action) => {
      logger.error('Error in fetchFastTrackPlanMetaData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.requestItem.isLoaderVisible = false;
        draftState.requestSummary.requestItem.hasError = true;
      });
      return newState;
    });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {
  setCoachingType, setCompanyName, setObjectives, setTermsAccepted, setAvailability, setReferrer, setSubscriptionTypeId, setSelectedSubscription, setIdentityResolved, setHourType,
} = coachingRequestSlice.actions;

export const getIdentityData = (state) => state.coachingRequestData.identityData.data;
export const getReferrerData = (state) => state.coachingRequestData.referrer;
export const isReferredUserLoaded = (state) => state.coachingRequestData.identityData.isReferredUserLoaded;
export const hasIdentityDataFetched = (state) => state.coachingRequestData.identityData.hasFetched;
export const isIdentityDataLoading = (state) => state.coachingRequestData.identityData.isLoaderVisible;
export const isLoaderVisible = (state) => state.coachingRequestData.isLoaderVisible;
export const IsLoggedInButNotEligible = (state) => {
  const data = state.coachingRequestData?.identityData?.data;
  if (data) {
    if (data.isAdministrator === false && data.isAutodeskEmployee === false && data.isPremiumSubscriber === false && data.isEnterpriseAdmin === false && data.isEnterpriseSubscriber === false) {
      return true;
    }
  }
  return false;
};

export const isAccountLevelButNotAdmin = (state) => {
  const data = state.coachingRequestData?.identityData?.data;
  if (data) {
    if (data.isPremiumSubscriber === true && data.isAdministrator === false) {
      return true;
    }
    if (data.isEnterpriseAdmin === false && data.isEnterpriseSubscriber === true) {
      return true;
    }
  }
  return false;
};
export const isReferredNotEligible = (state) => {
  const data = state.coachingRequestData?.identityData?.data;
  if (data) {
    if (data.isAdministrator === false) {
      if (data.isPremiumSubscriber === false && data.isAdministrator === false && (data.isEnterpriseAdmin === true || data.isEnterpriseSubscriber === true)) {
        return true;
      }
    }
  }
  return false;
};
export const isSubscriptionTypePremium = (state) => (state?.coachingRequestData?.identityData?.data?.isAdministrator || state?.coachingRequestData?.identityData?.data?.isPremiumSubscriber) || false;
export const isSubscriptionTypeEnterprise = (state) => (state?.coachingRequestData?.identityData?.data?.isEnterpriseAdmin || state?.coachingRequestData?.identityData?.data?.isEnterpriseSubscriber) || false;
export const isAllowedToSubmitRequest = (state) => (state?.coachingRequestData?.identityData?.data?.isEnterpriseAdmin || state?.coachingRequestData?.identityData?.data?.isAdministrator) || false;
export const getSubscriptionTypeLabel = (state) => {
  if (isSubscriptionTypeEnterprise(state)) {
    return 'Enterprise';
  }
  if (state?.coachingRequestData?.identityData?.data?.isPremiumSubscriber) {
    return 'Premium';
  }
  if (state?.coachingRequestData?.identityData?.hasFetched) {
    return 'not eligible';
  }
  return undefined;
};
export const getAnalyticsLabelSubscriptionType = (state) => {
  /** all lowercased
   * Premium Admin (value = Premium Admin)
   * Enterprise Admin (value = Enterprise Admin)
   * Premium or Enterprise Non-Admin:
   * (value = Premium NonAdmin or Enterprise NonAdmin)
   * No Premium or Enterprise subscription (value = Not PremEnt)
   */
  const data = state.coachingRequestData?.identityData?.data;
  if (data?.isEnterpriseSubscriber) {
    if (data?.isEnterpriseAdmin) {
      return 'enterprise admin';
    }
    return 'enterprise nonadmin';
  }
  if (data?.isPremiumSubscriber) {
    if (data?.isAdministrator) {
      return 'premium admin';
    }
    return 'premium nonadmin';
  }
  if (state?.coachingRequestData?.identityData?.hasFetched) {
    return 'not prement';
  }
  return undefined;
};
export const isPremiumCustomer = (state) => (state.coachingRequestData?.identityData?.data?.isPremiumSubscriber && state.coachingRequestData?.identityData?.data?.isAdministrator) || false;
export const isEnterpriseCustomer = (state) => (state.coachingRequestData?.identityData?.data?.isEnterpriseSubscriber && state.coachingRequestData?.identityData?.data?.isEnterpriseAdmin) || false;
export const isAutodeskEmployee = (state) => (state.coachingRequestData?.identityData?.data?.isAutodeskEmployee) || false;
export const requestSummary = (state) => state.coachingRequestData?.requestSummary;
export const getCoachingType = (state) => state.coachingRequestData?.coachingTypeData?.data;
export const getEditorialData = (state) => state.coachingRequestData?.editorial;
export const getSubscriptionsData = (state) => state.coachingRequestData?.subscriptionData?.data?.data;
export const hasSubscriptionDataFetched = (state) => state.coachingRequestData?.subscriptionData?.hasFetched;
export const doesHaveSubscriptionData = (state) => state.coachingRequestData?.subscriptionData?.hasFetched && state.coachingRequestData?.subscriptionData?.data?.data?.length > 0;
export const hasSubscriptionFetchedAndSingle = (state) => state.coachingRequestData?.subscriptionData?.hasFetched && state.coachingRequestData?.subscriptionData?.data?.data?.length === 1;
export const getCourseDetailData = (state) => state.coachingRequestData?.requestSummary?.requestItem;
export const hasCourseDetail = (state) => state.coachingRequestData?.requestSummary?.requestItem?.meta?.type === 'learnLiveCourse';
export const hasFastTrackPlanDetail = (state) => state.coachingRequestData?.requestSummary?.requestItem?.meta?.type === 'fastTrackLearningPlan';
export const isPostingCoachingRequest = (state) => state.coachingRequestData?.postingCoachingRequest;
export const isSubscriptionDataLoading = (state) => state.coachingRequestData?.subscriptionData?.isLoaderVisible;
export const isIdentityResolved = (state) => state.coachingRequestData?.identityData?.isIdentityResolved;
export const hasSubscriptionDataFetchedError = (state) => state.coachingRequestData?.subscriptionData?.hasError;
export const hasRequestSummaryFastTrackType = (state) => state.coachingRequestData?.requestSummary?.coachingType?.type?.value === coachTypeConstants.FAST_TRACK;
export const selectFastTrackTopic = (state) => state.coachingRequestData?.requestSummary?.coachingType?.topic?.label ?? '';

export const isSubscriptionDataResolved = (state) => {
  // if not an enterprise user, wait for subscription data
  if (!isSubscriptionTypeEnterprise(state)) {
    // If data is still loading, wait for it to complete
    if (isSubscriptionDataLoading(state)) return false;
    // Should return true: Attempt completed (even with error)
    if (hasSubscriptionDataFetchedError(state)) {
      return true; // ← Attempt was made and resolved (with error)
    }
    // If subscription data has not been fetched, wait for it to complete
    if (!hasSubscriptionDataFetched(state)) return false;
  }
  return true;
};

export const selectCoachingSubtypesByType = (type) => (state) => {
  if (type === coachTypeConstants.FAST_TRACK) {
    return state.coachingRequestData?.fastTrackTopicsData?.data?.map((topicData) => ({
      id: topicData.id,
      label: topicData.title,
    })) || [];
  }
  return state.coachingRequestData?.coachingTypeData?.data?.find((item) => item.value === type)?.coachingSubtype || [];
};

export const selectCoachingSubtypesLoading = (type = coachTypeConstants.PRODUCT) => (state) => {
  if (type === coachTypeConstants.FAST_TRACK) {
    return !state.coachingRequestData?.fastTrackTopicsData?.hasFetched || state.coachingRequestData?.fastTrackTopicsData?.isLoaderVisible;
  }
  return false;
};

export const selectCoachingSubtypesSelected = (type = coachTypeConstants.PRODUCT) => (state) => {
  const summary = requestSummary(state);

  if (type === coachTypeConstants.PRODUCT) {
    return summary?.coachingType?.product.map((item) => item.label) || [];
  }
  return summary?.coachingType?.topic.label || '';
};

export const selectFastTrackTopicsHasFetched = (state) => state.coachingRequestData?.fastTrackTopicsData?.hasFetched;

export default coachingRequestSlice.reducer;
